import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Shield,
  Users,
  KeyRound,
  Check,
  Eye,
  ShieldCheck,
  Lock
} from 'lucide-react';
import { useFeature } from '../contexts/FeaturesContext';

import { api } from '../services/api';
import Support from './Support';
import RolesPermissionsManager from './RolesPermissionsManager';
import DerogationsManager from './DerogationsManager';
import DoubleAuthManager from './DoubleAuthManager';

const AccessManagement = () => {
  // États pour la gestion des données
  const [selectedTab, setSelectedTab] = useState('roles');
  // La double authentification est dans toutes les offres, mais le CIK peut la
  // fermer pour une école. Comme les autres modules non souscrits, l'onglet
  // reste visible et se cadenasse : un onglet qui disparaît ressemble à une panne.
  const doubleAuthOuverte = useFeature('module.double_auth');
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Journal d'activité de sécurité
  const [securityLogs, setSecurityLogs] = useState([
    {
      id: 1,
      date: '2024-04-18 14:32:45',
      user: 'admin@ecole.edu',
      action: 'Modification de droits',
      details: 'Ajout du droit "Voir les finances" au groupe "Direction"',
      ip: '192.168.1.24'
    },
    {
      id: 2,
      date: '2024-04-17 09:15:22',
      user: 'thomas.dubois@ecole.edu',
      action: 'Droits exceptionnels',
      details: 'Attribution du droit "Consulter les rapports financiers" à l\'utilisateur "Thomas Dubois"',
      ip: '192.168.1.45'
    },
    {
      id: 3,
      date: '2024-04-16 16:08:10',
      user: 'admin@ecole.edu',
      action: 'Création de groupe',
      details: 'Création du groupe de sécurité "Agent technique"',
      ip: '192.168.1.24'
    },
    {
      id: 4,
      date: '2024-04-15 11:42:37',
      user: 'admin@ecole.edu',
      action: 'Modification de droits',
      details: 'Révocation du droit "Supprimer des élèves" au groupe "Administration"',
      ip: '192.168.1.24'
    },
    {
      id: 5,
      date: '2024-04-14 08:30:16',
      user: 'thomas.dubois@ecole.edu',
      action: 'Droits exceptionnels',
      details: 'Attribution du droit "Modifier les emplois du temps" à l\'utilisateur "Lucas Martin"',
      ip: '192.168.1.45'
    }
  ]);

  // Charger les données au démarrage
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Simulation d'une API
        setTimeout(() => {
          setIsLoading(false);
        }, 800);

        // Dans une implémentation réelle, vous feriez quelque chose comme:
        // const groupsResponse = await api.securityGroups.getAll();
        // const usersResponse = await api.users.getAll();
        // if (groupsResponse.success) setSecurityGroups(groupsResponse.data);
        // if (usersResponse.success) setUsers(usersResponse.data);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Fonction pour ajouter un log de sécurité
  const addSecurityLog = (user, action, details) => {
    const newLog = {
      id: securityLogs.length + 1,
      date: new Date().toISOString().replace('T', ' ').substring(0, 19),
      user,
      action,
      details,
      ip: '192.168.1.24' // Dans une vraie application, vous récupéreriez l'IP réelle
    };

    setSecurityLogs([newLog, ...securityLogs]);
  };

  return (
    <div className="w-full">
      {/* Navigation par sections - Navigation stable */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">

        {/* Navigation avec largeur fixe et stable */}
        <div className="mb-4">
          <div className="w-full max-w-6xl">
            {/* Quatre onglets visibles : « Audit » plus bas est commenté. À
                réajuster si l'un d'eux revient. */}
            <TabsList className="bg-[#c66d59]/10 p-1 rounded-lg w-full grid grid-cols-4">
              <TabsTrigger
                value="roles"
                className="data-[state=active]:bg-[#c66d59] data-[state=active]:text-white flex items-center justify-center gap-2 min-w-0 px-2"
              >
                <Shield size={16} className="shrink-0" />
                <span className="text-xs lg:text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                  Rôles & Permissions
                </span>
              </TabsTrigger>

              <TabsTrigger
                value="users"
                className="data-[state=active]:bg-[#c66d59] data-[state=active]:text-white flex items-center justify-center gap-2 min-w-0 px-2"
              >
                <Users size={16} className="shrink-0" />
                <span className="text-xs lg:text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                  dérogations
                </span>
              </TabsTrigger>

              <TabsTrigger
                value="double-auth"
                className="data-[state=active]:bg-[#c66d59] data-[state=active]:text-white flex items-center justify-center gap-2 min-w-0 px-2"
              >
                {doubleAuthOuverte
                  ? <ShieldCheck size={16} className="shrink-0" />
                  : <Lock size={16} className="shrink-0" />}
                <span className="text-xs lg:text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                  Double authentification
                </span>
              </TabsTrigger>

              <TabsTrigger
                value="support"
                className="data-[state=active]:bg-[#c66d59] data-[state=active]:text-white flex items-center justify-center gap-2 min-w-0 px-2"
              >
                <KeyRound size={16} className="shrink-0" />
                <span className="text-xs lg:text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                  Centre de support
                </span>
              </TabsTrigger>

            </TabsList>
          </div>
        </div>

        {/* Contenu des onglets */}
        <TabsContent value="roles" className="mt-0">
          <RolesPermissionsManager addSecurityLog={addSecurityLog} />
        </TabsContent>

        {/* Les dérogations sont posées ici, et nulle part ailleurs. Le circuit
            de demande qui les accompagnait n'a jamais été raccordé — aucun
            écran ne permettait d'en déposer une — et a été retiré. */}
        <TabsContent value="users" className="mt-0">
          <DerogationsManager />
        </TabsContent>

        <TabsContent value="double-auth" className="mt-0">
          {doubleAuthOuverte ? (
            <DoubleAuthManager />
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-6">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-gray-600">
                <Lock size={18} />
              </div>
              <div className="min-w-0 text-sm text-gray-700">
                <p className="font-semibold text-gray-900">
                  La double authentification n'est pas active pour votre établissement
                </p>
                <p className="mt-1">
                  Le module est fermé dans votre licence : la connexion se fait avec le mot de passe
                  seul, pour tous les comptes. Les téléphones déjà enrôlés sont conservés et
                  retrouveront leur protection à la réouverture.
                </p>
                <p className="mt-1 text-gray-500">Contactez votre interlocuteur KLASYS pour la réactiver.</p>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="support" className="mt-0">
          <Support />
        </TabsContent>

      </Tabs>

      {/* Notification de succès */}
      {showSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-50 border border-green-100 text-green-800 p-4 rounded-lg shadow-lg flex items-center z-50">
          <Check className="mr-2" size={20} />
          <span>{successMessage}</span>
        </div>
      )}
    </div>
  );
};

export default AccessManagement;